// Business Logic Hooks

import { useEffect, useLayoutEffect, useState, useRef, useCallback, useDeferredValue, useTransition, useId, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { SearchResultsPresentationVm, toSearchResultsPresentationVm } from '../viewModels/search.vm';
import { useAnalytics } from '@/shared/features/analytics/presentation/hooks/useAnalytics';
import { trackSearchedForProduct } from '../../constants';
import { getCookie } from '@/core/utils/cookieUtils';

/**
 * Service response interface - defined in presentation layer
 * Presentation layer should not depend on application layer types
 * This interface describes the data structure expected from the service
 */
interface SearchSuggestionsResponse {
  trending?: {
    list: Array<{ name: string; url: string }>;
    trendingTitle: string;
  };
  suggestions: (string | { title: string })[];
  products: {
    productTitle: string;
    list: Array<{
      image: string;
      name: string;
      url: string;
      designId?: number | string;
      id?: number | string;
      productId?: number | string;
      sku?: number | string;
    }>;
  };
  searchText?: string;
}

// Define the service interface locally - presentation layer shouldn't depend on application layer
// Only search.build.ts should know about the concrete SearchService
interface SearchServiceInterface {
  getSuggestions(query: string, allowEmpty?: boolean): Promise<SearchSuggestionsResponse>;
}

export interface UseSearchReturn {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  isFocused: boolean;
  setIsFocused: (focused: boolean) => void;
  searchResults: SearchResultsPresentationVm | null;
  isLoading: boolean;
  handleSubmit: (e: FormEvent<HTMLFormElement>) => void;
  handleSuggestionClick: (text: string) => void;
  handleTrendingClick: (url: string) => void;
  handleProductClick: (url: string) => void;
  searchRef: React.RefObject<HTMLDivElement | null>;
  listId: string;
}

export interface UseSearchOptions {
  service: SearchServiceInterface;
  onSearch?: (query: string) => void;
}

const MIN_LOADING_DELAY = 1000; // Minimum 1 second loading time to prevent flickering

/** Same-origin product URLs go through the router; anything else needs a full navigation. */
function goToProductUrl(navigate: ReturnType<typeof useNavigate>, url: string): void {
  if (/^https?:\/\//i.test(url)) {
    window.location.href = url;
    return;
  }
  navigate(url);
}

export function useSearch({ service, onSearch }: UseSearchOptions): UseSearchReturn {
  const { analytics } = useAnalytics();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [searchResults, setSearchResults] = useState<SearchResultsPresentationVm | null>(null);
  const [isPending, startTransition] = useTransition();
  const searchRef = useRef<HTMLDivElement>(null);
  const listId = useId();
  const isMountedRef = useRef(true);
  const searchStartTimeRef = useRef<number | null>(null);
  const searchQueryRef = useRef(searchQuery);
  const searchResultsRef = useRef<SearchResultsPresentationVm | null>(null);
  const searchEventTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const [showLoader, setShowLoader] = useState(false);

  // Keep refs in sync so closures always read the latest values regardless of transition state.
  // useLayoutEffect runs synchronously after every render, before any effects read the refs.
  useLayoutEffect(() => {
    searchQueryRef.current = searchQuery;
    searchResultsRef.current = searchResults;
  });

  // Use deferred value for debouncing (React 19 pattern)
  const deferredQuery = useDeferredValue(searchQuery);

  // Fetch suggestions from API
  // Service uses RTK Query adapter internally, which provides automatic caching
  const fetchSuggestions = useCallback(async (query: string, allowEmpty = false) => {
    // Allow empty query only when explicitly requested (e.g., on focus)
    if (!query.trim() && !allowEmpty) {
      if (isMountedRef.current) {
        startTransition(() => {
          setSearchResults(null);
          setShowLoader(false);
        });
      }
      searchStartTimeRef.current = null;
      return;
    }

    // Record search start time and show loader
    const startTime = Date.now();
    searchStartTimeRef.current = startTime;
    setShowLoader(true);

    startTransition(() => {
      // Mark as pending
    });

    try { 
      // Service uses RTK Query adapter which automatically caches responses
      // Same query will return cached data instantly without API call
      const appResults = await service.getSuggestions(query, allowEmpty);
      
      // Combine granular application VMs into presentation VM
      const presentationResults = toSearchResultsPresentationVm(
        appResults.trending,
        appResults.suggestions,
        appResults.products,
        appResults.searchText,
        query
      );
      
      // Calculate elapsed time
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, MIN_LOADING_DELAY - elapsedTime);
      
      // Wait for minimum delay before showing results
      await new Promise(resolve => setTimeout(resolve, remainingTime));
      
      // Only update if this is still the latest search
      if (isMountedRef.current && searchStartTimeRef.current === startTime) {
        startTransition(() => {
          setSearchResults(presentationResults);
          setShowLoader(false);
        });
        if (appResults.products?.list?.length) {
          clearTimeout(searchEventTimeoutRef.current);
          searchEventTimeoutRef.current = setTimeout(() => {
            if (!query) return;
            const contentIds = appResults.products.list
              .slice(0, 9)
              .map((item) => item.designId ?? item.id ?? item.productId ?? item.sku)
              .filter((id) => id != null && id !== '')
              .map(String);
            const sortOrder = getCookie('sortingPreference');
            analytics?.trackEvent(
              'Search',
              {
                content_type: 'product',
                content_ids: contentIds,
                search_source: 'suggestion',
                result_count: appResults.products.list.length,
                search_string: query,
                ...(sortOrder ? { sort_order: sortOrder } : {}),
              },
              { eventProviders: ['facebook', 'openai'] },
            );
          }, 1000);
        }
      }
    } catch (error) {
      console.error('Failed to fetch suggestions:', error);
      
      // Calculate elapsed time for error case too
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, MIN_LOADING_DELAY - elapsedTime);
      
      await new Promise(resolve => setTimeout(resolve, remainingTime));
      
      if (isMountedRef.current && searchStartTimeRef.current === startTime) {
        startTransition(() => {
          setSearchResults(null);
          setShowLoader(false);
        });
      }
    }
  }, [analytics, service, startTransition]);

  // Fetch on focus (even with empty query) — only fires when focus state changes, not on every keystroke
  // Note: fetchSuggestions is async and updates state, but this is necessary for the search functionality
  useEffect(() => {
    if (isFocused) {
      // Use setTimeout to defer the async call after render
      const timeoutId = setTimeout(() => {
        void fetchSuggestions(searchQueryRef.current, true);
      }, 0);
      return () => clearTimeout(timeoutId);
    } else {
      // Reset loader when not focused
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Required for cleanup on blur
      setShowLoader(false);
      searchStartTimeRef.current = null;
    }
  }, [isFocused, fetchSuggestions]);

  // Fetch suggestions when deferred query changes (React 19 pattern)
  // Note: fetchSuggestions is async and updates state, but this is necessary for the search functionality
  useEffect(() => {
    if (isFocused && deferredQuery.trim()) {
      // Use setTimeout to defer the async call after render
      const timeoutId = setTimeout(() => {
        void fetchSuggestions(deferredQuery, false);
      }, 500);
      return () => clearTimeout(timeoutId);
    } else if (!deferredQuery.trim() && !isFocused) {
      if (isMountedRef.current) {
        // eslint-disable-next-line react-hooks/set-state-in-effect -- Required for cleanup when query is empty
        setSearchResults(null);
      }
    }
  }, [deferredQuery, isFocused, fetchSuggestions]);

  useEffect(() => {
    // Close dropdown when clicking outside
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsFocused(false);
      }
    };

    if (isFocused) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isFocused]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      clearTimeout(searchEventTimeoutRef.current);
    };
  }, []);

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // Read from ref to get latest results even if startTransition hasn't committed yet
    const productList = searchResultsRef.current?.products?.list;
    if (productList?.length === 1 && productList[0].url) {
      trackSearchedForProduct(analytics, searchQuery);
      goToProductUrl(navigate, productList[0].url);
      setIsFocused(false);
      return;
    }
    if (onSearch) {
      onSearch(searchQuery);
    } else {
      console.log('Search:', searchQuery);
    }
    setIsFocused(false);
  };

  const handleTrendingClick = (url: string) => {
    if (onSearch) {
      onSearch(url);
    }
    setIsFocused(false);
  };

  const handleSuggestionClick = useCallback(async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    setSearchQuery(trimmed);
    setIsFocused(false);
    // Fetch results for the suggestion text to check for a single-product match.
    try {
      const results = await service.getSuggestions(trimmed, false);
      const productList = results.products?.list;
      if (productList?.length === 1 && productList[0].url) {
        trackSearchedForProduct(analytics, trimmed);
        goToProductUrl(navigate, productList[0].url);
        return;
      }
    } catch {
      // Fall through to normal search on error
    }
    if (onSearch) onSearch(trimmed);
  }, [service, onSearch, analytics, navigate]);

  const handleProductClick = (url: string) => {
    if (onSearch) {
      onSearch(url);
    }
    setIsFocused(false);
  };

  return {
    searchQuery,
    setSearchQuery,
    isFocused,
    setIsFocused,
    searchResults,
    isLoading: showLoader || isPending, // Show loader if explicitly set or pending
    handleSubmit,
    handleSuggestionClick,
    handleTrendingClick,
    handleProductClick,
    searchRef,
    listId,
  };
}

